import { clsx } from 'clsx'

import {
  BillingPriceInterval,
  type BillingPrice,
} from '@tribeplatform/gql-client/types'
import { T } from '@tribeplatform/react-components/i18n'
import { Badge } from '@tribeplatform/react-ui-kit/Badge'

interface Props {
  interval: BillingPriceInterval
  monthlyAmount: number
  monthlyPrice?: BillingPrice
  reservedMonthlyAmount: number
  reservedSavingsPercent: number | null
  savingsPercent: number | null
  yearlyTotal: number | null
}

export const PlanPricing = ({
  interval,
  monthlyAmount,
  monthlyPrice,
  reservedMonthlyAmount,
  reservedSavingsPercent,
  savingsPercent,
  yearlyTotal,
}: Props) => {
  const shouldReserveSavingsRow =
    reservedSavingsPercent > 0 && Boolean(monthlyPrice)
  const shouldShowSavingsRow =
    interval === BillingPriceInterval.Year && savingsPercent > 0 && monthlyPrice

  return (
    <div>
      {shouldReserveSavingsRow && (
        <div className="grid">
          <div
            aria-hidden
            className="invisible col-start-1 row-start-1 h-0 overflow-hidden whitespace-nowrap"
          >
            <span className="text-[13px] text-content-subdued line-through">
              <T
                defaultMessage="{currency}{amount}"
                id="Admin.Billing.PlanCards.MonthlyPrice"
                values={{
                  currency: '$',
                  amount: (monthlyPrice.amount / 100).toLocaleString(),
                }}
              />
            </span>
            <Badge variant="positive" size="sm">
              <T
                defaultMessage="Save {percent}%"
                id="Admin.Billing.PlanCards.SavePercent"
                values={{ percent: reservedSavingsPercent }}
              />
            </Badge>
          </div>
          <div
            aria-hidden={!shouldShowSavingsRow}
            className={clsx(
              'col-start-1 row-start-1 overflow-hidden transition-all duration-200 ease-out',
              {
                'mb-2 max-h-8 opacity-100': shouldShowSavingsRow,
                'max-h-0 opacity-0': !shouldShowSavingsRow,
              },
            )}
          >
            <div className="flex items-center gap-2">
              <span className="text-[13px] text-content-subdued line-through">
                <T
                  defaultMessage="{currency}{amount}"
                  id="Admin.Billing.PlanCards.MonthlyPrice"
                  values={{
                    currency: '$',
                    amount: (monthlyPrice.amount / 100).toLocaleString(),
                  }}
                />
              </span>
              <Badge variant="positive" size="sm">
                <T
                  defaultMessage="Save {percent}%"
                  id="Admin.Billing.PlanCards.SavePercent"
                  values={{ percent: savingsPercent }}
                />
              </Badge>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-baseline gap-1 justify-between">
        <span className="grid">
          <span
            aria-hidden
            className="invisible col-start-1 row-start-1 text-[2.125rem] font-semibold tracking-[-0.03em] tabular-nums text-content"
          >
            <T
              defaultMessage="{currency}{amount}/m"
              id="Admin.Billing.PlanCards.MonthlyAmount"
              values={{
                currency: '$',
                amount: Math.round(reservedMonthlyAmount).toLocaleString(),
              }}
            />
          </span>
          <span className="col-start-1 row-start-1 text-[2.125rem] font-semibold tracking-[-0.03em] tabular-nums text-content">
            <T
              defaultMessage="{currency}{amount}/m"
              id="Admin.Billing.PlanCards.MonthlyAmount"
              values={{
                currency: '$',
                amount: Math.round(monthlyAmount).toLocaleString(),
              }}
            />
          </span>
        </span>
        {yearlyTotal !== null && (
          <span className="text-xs tabular-nums text-content-subdued">
            <T
              defaultMessage="({currency}{amount}/year)"
              id="Admin.Billing.PlanCards.YearlyTotal"
              values={{
                currency: '$',
                amount: Math.round(yearlyTotal).toLocaleString(),
              }}
            />
          </span>
        )}
      </div>
    </div>
  )
}
